import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, ExternalLink } from "lucide-react";
import freshdropImg from "../../assets/freshdropimage.png";
import dakar360Img from "../../assets/dakar360.png";

const projects = [
  {
    id: "freshdrop",
    num: "01",
    title: "FreshDrop",
    status: "completed",
    description:
      "A full-stack e-commerce platform built for the Kenyan market featuring M-Pesa payment integration, real-time order tracking, and a complete vendor management system.",
    meta: "Full-stack · E-commerce · Payments",
    image: freshdropImg,
    techStack: [
      { name: "Next.js", type: "frontend" },
      { name: "Tailwind CSS", type: "frontend" },
      { name: "Redux", type: "frontend" },
      { name: "AdonisJS", type: "backend" },
      { name: "MySQL", type: "backend" },
    ],
  },
  {
    id: "dakar360",
    num: "02",
    title: "Dakar360",
    status: "ongoing",
    description:
      "An integrated business management solution built for growing businesses. From sales and inventory to accounting and compliance, Dakar360 brings all your essential tools into one easy-to-use platform accessible via web or mobile",
    meta: "Full-stack · Inventory · Invoicing · Order Management",
    image: dakar360Img,
    techStack: [
      { name: "Next.js", type: "frontend" },
      { name: "Tailwind CSS", type: "frontend" },
      { name: "Redux", type: "frontend" },
      { name: "Laravel", type: "backend" },
      { name: "PostgreSQL", type: "backend" },
    ],
  },
];

export default function ProjectsPreview() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-4 sm:py-8 lg:py-10 bg-white overflow-hidden"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          className={`text-center mb-8 sm:mb-10 transition-all duration-700 ease-out ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 px-4 py-1.5 rounded-full mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
            <span className="text-blue-600 text-xs sm:text-sm font-medium tracking-wide">
              My work
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium text-black mb-4">
            Featured <span className="text-blue-600">projects</span>
          </h2>
          <p className="text-gray-500 text-sm sm:text-base md:text-lg leading-relaxed max-w-lg mx-auto">
            Real products built end-to-end from database design to the final
            product. Here's what I've built.
          </p>
        </div>

        {/* Project Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {projects.map((project, index) => (
            <div
              key={project.id}
              className={`transition-all duration-700 ease-out ${
                isVisible
                  ? "opacity-100 translate-x-0"
                  : index === 0
                  ? "opacity-0 -translate-x-16"
                  : "opacity-0 translate-x-16"
              }`}
              style={{
                transitionDelay: isVisible ? `${index * 100}ms` : "0ms",
              }}
            >
              <Link to={`/projects`} className="block group h-full">
                <div className="border border-gray-100 rounded-2xl overflow-hidden bg-white hover:border-blue-200 transition-colors duration-300 h-full flex flex-col">
                  {/* Image */}
                  <div className="relative h-48 md:h-56 overflow-hidden bg-gray-100">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/45 transition-all duration-300 flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 inline-flex items-center gap-2 bg-white text-black text-sm font-medium px-5 py-2 rounded-full">
                        View project <ExternalLink size={13} />
                      </span>
                    </div>
                    <span
                      className={`absolute top-3.5 right-3.5 inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full ${
                        project.status === "completed"
                          ? "bg-black text-white"
                          : "bg-blue-600 text-white"
                      }`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70"></span>
                      {project.status === "completed"
                        ? "Completed"
                        : "In progress"}
                    </span>
                  </div>

                  {/* Body */}
                  <div className="p-6 md:p-7 flex flex-col flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg sm:text-xl md:text-2xl font-medium text-black">
                        {project.title}
                      </h3>
                      <span className="text-[11px] md:text-xs font-medium text-blue-300 tracking-widest mt-1">
                        {project.num}
                      </span>
                    </div>
                    <p className="text-sm sm:text-[15px] md:text-base text-gray-500 leading-relaxed mb-5">
                      {project.description}
                    </p>

                    <p className="text-[10px] md:text-[11px] font-medium text-gray-400 tracking-widest uppercase mb-3">
                      Tech stack
                    </p>
                    <div className="flex flex-wrap gap-2 mb-6">
                      {project.techStack.map((tech, i) => (
                        <span
                          key={i}
                          className={`text-xs md:text-[13px] font-medium px-3 py-1 rounded-full border ${
                            tech.type === "frontend"
                              ? "bg-blue-50 text-blue-700 border-blue-100"
                              : "bg-gray-50 text-gray-600 border-gray-100"
                          }`}
                        >
                          {tech.name}
                        </span>
                      ))}
                    </div>

                    <div className="border-t border-gray-100 pt-4 flex items-center justify-between mt-auto">
                      <span className="text-xs md:text-sm text-gray-400">
                        {project.meta}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-xs md:text-sm font-medium text-blue-600 group-hover:gap-2.5 transition-all">
                        View details <ArrowRight size={13} />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
